/** KPI-карточки сводки: значение, было, Δ ₽ и Δ % (у маржи % — Δ п.п.).
 *  «Расходы» и «Себестоимость» — расход: показываются положительными, Δ — по показанному, рост —
 *  плохо (соглашение — `calc.ts`). */
import type { OzSummary } from "@/api/ozonAnalytics";
import {
  COGS_GROUP_LABEL,
  COGS_HINT,
  cogsNullTitle,
  cogsTotal,
  EXPENSES_LABEL,
  expensesHint,
  NET_MARGIN_LABEL,
  pricesAt,
} from "./cogs";
import {
  delta,
  deltaTone,
  share,
  shareDelta,
  shown,
  shownTitle,
  type Kind,
  type Tone,
} from "./calc";
import { int, intSigned, pct, pctSigned, pp, rub, rubSigned } from "./format";
import type { MpConfig } from "./mp";

export interface Kpi {
  title: string;
  cur: string;
  /** подсказка к значению (отрицательный расход — «уменьшает расходы») */
  curTitle: string | null;
  was: string | null;
  /** подсказка к «было» — как у значения */
  wasTitle: string | null;
  deltaAbs: string | null;
  deltaRel: string | null;
  tone: Tone;
  hint: string;
  /** почему значения нет — показывается прямо в карточке под «—» (не только подсказкой) */
  note?: string | null;
}

/** KPI суммы: cur/was — в знаке витрины (was undefined — сравнения нет); показываются и
 *  сравниваются в знаке рода kind. */
export function moneyKpi(
  title: string,
  kind: Kind,
  cur: number,
  was: number | undefined,
  hint: string,
): Kpi {
  const v = shown(cur, kind);
  const w = was === undefined ? undefined : shown(was, kind);
  const d = w !== undefined ? delta(v, w) : null;
  return {
    title,
    cur: rub(v),
    curTitle: shownTitle(v, kind),
    was: w !== undefined ? rub(w) : null,
    wasTitle: w !== undefined ? shownTitle(w, kind) : null,
    deltaAbs: d ? rubSigned(d.abs) : null,
    deltaRel: d ? pctSigned(d.rel) : null,
    tone: deltaTone(d?.abs, 0.5, kind),
    hint,
  };
}

/** KPI суммы, которой может не быть (себестоимость: null — период раньше `cogs_from`, у площадки
 *  её нет): «—» и причина прямо в карточке (`note`: `nullTitle` / `nullTitleCmp`), Δ — только когда
 *  есть оба числа. Нет обоих чисел — строки «было» нет: «было —» ничего не объясняет. */
export function optMoneyKpi(
  title: string,
  kind: Kind,
  cur: number | null,
  was: number | null | undefined,
  hint: string,
  nullTitle: string,
  nullTitleCmp: string,
): Kpi {
  if (cur !== null && (was === undefined || was !== null)) {
    return moneyKpi(title, kind, cur, was, hint);
  }
  const hasCmp = was !== undefined;
  const v = cur === null ? null : shown(cur, kind);
  const w = was === undefined || was === null ? null : shown(was, kind);
  const showWas = hasCmp && !(v === null && w === null);
  return {
    title,
    cur: v === null ? "—" : rub(v),
    curTitle: v === null ? nullTitle : shownTitle(v, kind),
    was: showWas ? (w === null ? "—" : rub(w)) : null,
    wasTitle: showWas ? (w === null ? nullTitleCmp : shownTitle(w, kind)) : null,
    deltaAbs: showWas ? "—" : null,
    deltaRel: null,
    tone: "neutral",
    hint,
    note: v === null ? nullTitle : w === null && hasCmp ? nullTitleCmp : null,
  };
}

/** KPI сводки по схеме «Выручка − Расходы = Маржа» (решение владельца 22.10.2026): выручка,
 *  «Расходы» (все удержания площадки + себестоимость, `expenses`), «Маржа» (`net_margin`), маржа %
 *  (Δ в п.п.), штуки, «Себестоимость» (входит в расходы). Маржи до себестоимости нет. Без
 *  себестоимости (период раньше `cogs_from`, цен нет, площадка без неё) «Расходы», «Маржа», маржа %
 *  и себестоимость — «—» с причиной в карточке, а не удержания / маржа до себестоимости.
 *  `mp` — площадка: подписи и пояснения к выручке и штукам. */
export function buildKpis(s: OzSummary, mp: Pick<MpConfig, "label" | "kpiHints">): Kpi[] {
  const c = s.totals.cur;
  const p = s.totals.cmp;
  const nCur = cogsNullTitle(s.cogs, mp.label, "cur");
  const nCmp = cogsNullTitle(s.cogs, mp.label, "cmp");
  const prices = pricesAt(s.cogs);
  const exp = c.expenses ?? null;
  const expCmp = p ? (p.expenses ?? null) : null;
  const net = c.net_margin ?? null;
  const netCmp = p ? (p.net_margin ?? null) : null;
  const pctOf = (x: number | null, rev: number): number | null =>
    x === null ? null : share(x, rev);
  const nmCur = pctOf(net, c.revenue);
  const nmCmp = p ? pctOf(netCmp, p.revenue) : null;
  const nmDiff = p ? shareDelta(nmCur, nmCmp) : null;
  const cogsAll = cogsTotal(c.cogs_goods, c.cogs_wh);
  const cogsAllCmp = p ? cogsTotal(p.cogs_goods, p.cogs_wh) : undefined;
  const qd = p ? delta(c.qty, p.qty) : null;
  const expHint =
    exp === null || cogsAll === null
      ? expensesHint(mp.label)
      : `${pct(share(shown(exp, "expense"), c.revenue))} выручки: удержания ${mp.label} ` +
        `${rub(shown(c.deductions, "expense"))} + себестоимость ${rub(shown(cogsAll, "expense"))}`;
  const bothNull = net === null && netCmp === null;
  const pctKpi: Kpi = {
    title: `${NET_MARGIN_LABEL}, % выручки`,
    cur: net === null ? "—" : pct(nmCur),
    curTitle: net === null ? nCur : null,
    was: p && !bothNull ? (netCmp === null ? "—" : pct(nmCmp)) : null,
    wasTitle: p && netCmp === null ? nCmp : null,
    deltaAbs: p && !bothNull ? pp(nmDiff) : null,
    deltaRel: null,
    tone: deltaTone(nmDiff, 0.0005),
    hint: "маржа / выручка",
    note: net === null ? nCur : p && netCmp === null ? nCmp : null,
  };
  return [
    moneyKpi("Выручка", "income", c.revenue, p?.revenue, mp.kpiHints.revenue),
    optMoneyKpi(EXPENSES_LABEL, "expense", exp, p ? expCmp : undefined, expHint, nCur, nCmp),
    optMoneyKpi(
      NET_MARGIN_LABEL,
      "income",
      net,
      p ? netCmp : undefined,
      "выручка − расходы, до налогов",
      nCur,
      nCmp,
    ),
    pctKpi,
    {
      title: "Продано, шт.",
      cur: int(c.qty),
      curTitle: null,
      was: p ? int(p.qty) : null,
      wasTitle: null,
      deltaAbs: qd ? intSigned(qd.abs) : null,
      deltaRel: qd ? pctSigned(qd.rel) : null,
      tone: deltaTone(qd?.abs),
      hint: mp.kpiHints.qty,
    },
    optMoneyKpi(
      COGS_GROUP_LABEL,
      "expense",
      cogsAll,
      cogsAllCmp,
      `входит в расходы: ${COGS_HINT}${prices ? ` · ${prices}` : ""}`,
      nCur,
      nCmp,
    ),
  ];
}

/** Средняя цена продажи = выручка / продано шт. (карточка артикула). Штук ≤ 0 (одни возвраты
 *  или нет продаж) — цены нет: «—», Δ не считается. */
export function avgPriceKpi(s: OzSummary): Kpi {
  const avg = (t: { revenue: number; qty: number }): number | null =>
    t.qty > 0 ? t.revenue / t.qty : null;
  const c = avg(s.totals.cur);
  const p = s.totals.cmp ? avg(s.totals.cmp) : null;
  const d = s.totals.cmp && c !== null && p !== null ? delta(c, p) : null;
  return {
    title: "Средняя цена",
    cur: c === null ? "—" : rub(c),
    curTitle: null,
    was: s.totals.cmp ? (p === null ? "—" : rub(p)) : null,
    wasTitle: null,
    deltaAbs: s.totals.cmp ? (d ? rubSigned(d.abs) : "—") : null,
    deltaRel: d ? pctSigned(d.rel) : null,
    tone: deltaTone(d?.abs, 0.5, "income"),
    hint: "выручка / продано шт.",
  };
}
